import { useCallback, useEffect, useState } from "react";
import type { EqmsController } from "../../../controllers/useEqms";
import type { Attachment } from "../../../models/eqms";
import { deleteAttachment, downloadAttachment, fetchAttachmentBytes, listAttachments, uploadAttachmentFile } from "../../../controllers/eqmsApi";
import { DocPreviewModal } from "../../components/DocPreviewModal";
import { friendlyMessage } from "../../../controllers/projectApi";
import { FileDrop } from "../../components/FileDrop";

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// 자료함: 프로젝트 단위 첨부파일 목록 + 업로드/다운로드/삭제.
export function EqmsAttachmentsView({ eqms }: { eqms: EqmsController }) {
  const projectId = eqms.projectId;
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [previewing, setPreviewing] = useState<Attachment | null>(null);

  const reload = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      setAttachments(await listAttachments(eqms.getToken, "PROJECT", projectId));
    } catch (e) {
      setError(friendlyMessage(e));
    } finally {
      setLoading(false);
    }
  }, [eqms.getToken, projectId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  async function handleUpload(file: File) {
    if (!projectId) return;
    setUploading(true);
    setError(null);
    try {
      await uploadAttachmentFile(eqms.getToken, "PROJECT", projectId, file);
      await reload();
    } catch (e) {
      setError(friendlyMessage(e));
    } finally {
      setUploading(false);
    }
  }

  async function remove(id: string) {
    setError(null);
    try {
      await deleteAttachment(eqms.getToken, id);
      setAttachments((cur) => cur.filter((a) => a.id !== id));
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  async function download(a: Attachment) {
    setError(null);
    try {
      await downloadAttachment(eqms.getToken, a.id, a.filename);
    } catch (e) {
      setError(friendlyMessage(e));
    }
  }

  if (!projectId) {
    return <div className="note">프로젝트를 먼저 선택하세요.</div>;
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h3 style={{ margin: 0 }}>자료함</h3>
      </div>

      {/* A drop target rather than a button in the header: this screen is a place you bring files to,
          and a hidden input behind a button gave no way to just drag one in. */}
      <div style={{ marginBottom: 16 }}>
        <FileDrop
          disabled={uploading}
          onFile={(f) => void handleUpload(f)}
          label={uploading ? "업로드 중…" : "파일을 여기에 끌어다 놓으세요"}
        />
      </div>

      {error && (
        <div className="note" style={{ background: "#fdeef0", marginBottom: 16 }}>
          <b style={{ color: "var(--amber)" }}>오류:</b> {error}
        </div>
      )}

      {loading ? (
        <div className="center">불러오는 중…</div>
      ) : (
        <table className="sdui-table">
          <thead>
            <tr>
              <th>파일명</th>
              <th>크기</th>
              <th>업로드일</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {attachments.map((a) => (
              <tr key={a.id}>
                <td>
                  {/* 파일명 자체가 미리보기 진입점. */}
                  <button className="filename-link" onClick={() => setPreviewing(a)}>{a.filename}</button>
                </td>
                <td className="mono">{formatSize(a.size)}</td>
                <td className="mono">{a.createdAt.slice(0, 10)}</td>
                <td>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button className="btn" style={{ width: "auto" }} onClick={() => void download(a)}>
                      다운로드
                    </button>
                    <button className="btn" style={{ width: "auto" }} onClick={() => void remove(a.id)}>
                      삭제
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
          {attachments.length === 0 && (
            <tfoot>
              <tr>
                <td colSpan={4} className="sdui-table-empty">
                  첨부파일이 없습니다.
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      )}

      {previewing && (
        <DocPreviewModal
          filename={previewing.filename}
          format={previewing.contentType}
          load={() => fetchAttachmentBytes(eqms.getToken, previewing.id)}
          onDownload={() => void download(previewing)}
          onClose={() => setPreviewing(null)}
        />
      )}
    </div>
  );
}
